import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../utils/baseQueryWithAuth';
import {
  buildSalonThemeApplyRequest,
  buildSalonThemeTargetsRequest,
} from './salonThemeRequests';
import {
  buildSalonAppearanceApplyRequest,
  buildSalonAppearanceResetRequest,
  buildSalonAppearanceRetrieveRequest,
  buildSalonAppearanceTargetsRequest,
  buildSalonAppearanceUpdateRequest,
} from './salonAppearanceRequests';

export type SalonFontKey =
  | 'saloone'
  | 'modern'
  | 'soft'
  | 'classic'
  | 'open_sans'
  | 'lato'
  | 'poppins'
  | 'montserrat'
  | 'raleway'
  | 'playfair_display'
  | 'cairo'
  | 'tajawal';

export type SalonPublicTemplateKey =
  | 'saloone_signature'
  | 'atelier_muse'
  | 'maison_eclat'
  | 'pure_studio'
  | 'harmonie_clinique'
  | 'gloss_lab'
  | 'cercle_heritage'
  | 'ecrin_serein'
  | 'neo_derma';

export interface SalonThemeConfig {
  version: 1;
  primary_color: string;
  secondary_color: string;
  background_color: string;
  font_key: SalonFontKey;
}

export interface SalonThemeState {
  stored: Partial<SalonThemeConfig>;
  resolved: SalonThemeConfig;
}

export interface SalonThemeTarget {
  salon_id: number;
  name: string;
}

export interface SalonThemeTargetsResponse {
  current_salon_id: number;
  targets: SalonThemeTarget[];
}

export interface SalonThemeApplyResponse extends SalonThemeState {
  updated_count: number;
  updated_salon_ids: number[];
}

export type SalonButtonAppearance = 'filled' | 'outlined' | 'soft';
export type SalonButtonShape = 'square' | 'rounded' | 'pill';
export type SalonButtonSize = 'compact' | 'standard' | 'large';
export type SalonHeaderComposition = 'horizontal' | 'centered' | 'compact';
export type SalonHeaderSurface = 'light' | 'brand' | 'transparent';
export type SalonHeaderBehavior = 'static' | 'sticky';
export type SalonContentAlignment = 'start' | 'center' | 'end';
export type SalonServicesLayout = 'grid' | 'list';
export type SalonServiceColumns = 2 | 3 | 4;
export type SalonSidebarPosition = 'start' | 'end' | 'stacked';
export type SalonStyleFontKey = SalonFontKey | 'inherit';
export type SalonTextSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type SalonTextWeight = 400 | 500 | 600 | 700 | 800;
export type SalonTextAlignment = 'start' | 'center' | 'end';
export type SalonTextTransform = 'none' | 'uppercase';
export type SalonLetterSpacing = 'normal' | 'wide';
export type SalonStyleColor = string | 'inherit';
export type SalonSectionStyleKey =
  | 'header'
  | 'gallery'
  | 'services'
  | 'team'
  | 'location'
  | 'opening_hours'
  | 'reviews';
export type SalonVisibilityKey = Exclude<
  SalonSectionStyleKey,
  'header' | 'services'
>;

export interface SalonTextStyle {
  font_key: SalonStyleFontKey;
  size: SalonTextSize;
  weight: SalonTextWeight;
  italic: boolean;
  color: SalonStyleColor;
  alignment: SalonTextAlignment;
  transform: SalonTextTransform;
  letter_spacing: SalonLetterSpacing;
}

export interface SalonPublicLayoutConfig {
  version: 3;
  buttons: {
    appearance: SalonButtonAppearance;
    shape: SalonButtonShape;
    size: SalonButtonSize;
    background_color: SalonStyleColor;
    border_color: SalonStyleColor;
    text_color: SalonStyleColor;
    font_key: SalonStyleFontKey;
    text_size: SalonTextSize;
    weight: SalonTextWeight;
    italic: boolean;
    transform: SalonTextTransform;
    letter_spacing: SalonLetterSpacing;
  };
  header: {
    composition: SalonHeaderComposition;
    surface: SalonHeaderSurface;
    behavior: SalonHeaderBehavior;
  };
  content: {
    alignment: SalonContentAlignment;
    services: SalonServicesLayout;
    service_columns: SalonServiceColumns;
    sidebar: SalonSidebarPosition;
  };
  section_styles: Record<
    SalonSectionStyleKey,
    { title: SalonTextStyle; content: SalonTextStyle }
  >;
  visibility: Record<SalonVisibilityKey, boolean>;
}

export interface SalonAppearance {
  template_key: SalonPublicTemplateKey;
  theme: SalonThemeConfig;
  layout: SalonPublicLayoutConfig;
}

export interface SalonAppearanceState {
  stored: {
    template_key: SalonPublicTemplateKey;
    theme: Partial<SalonThemeConfig>;
    layout: Partial<SalonPublicLayoutConfig>;
  };
  resolved: SalonAppearance;
}

export interface SalonAppearanceTargetsResponse {
  current_salon_id: number;
  targets: SalonThemeTarget[];
}

export interface SalonAppearanceApplyResponse extends SalonAppearanceState {
  updated_count: number;
  updated_salon_ids: number[];
}

// Types pour les réponses API des salons
export interface Salon {
  salon_id: number;
  name: string;
  localized_name?: string;
  name_translations?: Record<string, string>;
  address: string;
  localized_address?: string;
  address_translations?: Record<string, string>;
  description?: string;
  localized_description?: string;
  description_translations?: Record<string, string>;
  phone?: string;
  email?: string;
  owner?: number;
  latitude?: number;
  longitude?: number;
  opening_hours?: Record<string, { ouverture: string, fermeture: string }>;
  public_theme?: SalonThemeConfig;
  public_layout?: SalonPublicLayoutConfig;
  public_template_key?: SalonPublicTemplateKey;
  photos?: {
    photo_id: number;
    url?: string;
    image_url?: string;
    caption?: string;
    is_primary: boolean;
  }[];
}

// Types pour les favoris
export interface FavoriteCheckResponse {
  is_favorite: boolean;
  salon_id: number;
}

export interface FavoriteToggleResponse {
  action: 'added' | 'removed';
  is_favorite: boolean;
  salon_id: number;
  message: string;
}

export interface FavoriteItem {
  id: number;
  salon: Salon;
  salon_id: number;
  client: number;
  created_at: string;
}

// Types pour les disponibilités
export interface AvailableSlot {
  start_time: string;
  end_time: string;
  duration_minutes: number;
}

export interface TeamMemberAvailability {
  team_member_id: number;
  team_member_name: string;
  available_slots: AvailableSlot[];
}

export interface ServiceAvailability {
  service_id: number;
  service_name: string;
  duration_minutes: number;
  team_members: TeamMemberAvailability[];
}

export interface SalonAvailabilityResponse {
  salon_id: number;
  salon_name: string;
  date: string;
  services: ServiceAvailability[];
  teams?: any[];
  debug_info?: any;
}

export interface SalonQrResponse {
  salon_id: number;
  public_url: string;
  qr_code_data_uri: string;
}

export interface SalonClosure {
  closure_id: number;
  salon: number;
  is_recurring: boolean;
  start_datetime?: string | null;
  end_datetime?: string | null;
  month?: number | null;
  day?: number | null;
  start_time?: string | null;
  end_time?: string | null;
  reason?: string;
  status: 'active' | 'cancelled';
  created_by?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface SalonClosureCreateRequest {
  is_recurring: boolean;
  start_datetime?: string | null;
  end_datetime?: string | null;
  month?: number | null;
  day?: number | null;
  start_time?: string | null;
  end_time?: string | null;
  reason?: string;
  status?: 'active' | 'cancelled';
}

export interface LocationCandidate {
  formatted_address: string;
  latitude: number;
  longitude: number;
  country_code: string;
  country_name: string;
  admin_area_1: string;
  admin_area_2: string;
  locality: string;
  postal_code: string;
  provider: string;
  provider_place_id: string;
  raw: Record<string, unknown>;
}

export interface LocationSuggestionsResponse {
  results: LocationCandidate[];
}

export interface ReverseLocationResponse {
  candidate: LocationCandidate;
}

export interface SalonSearchSuggestion {
  kind: 'salon' | 'place';
  source: 'local' | 'external';
  label: string;
  salon_id?: number;
  place_level?: 'country' | 'admin_area_1' | 'admin_area_2' | 'locality' | 'postal_code';
  place_value?: string;
  place_country_code?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface SalonSearchRequest {
  q?: string;
  category_id?: number;
  radius_km?: number;
  latitude?: number;
  longitude?: number;
  mode?: 'text' | 'nearby';
  page?: number;
  page_size?: number;
  place_level?: string;
  place_value?: string;
  place_country_code?: string;
  place_text?: string;
}

export interface SalonSearchResponse {
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  next_page: number | null;
  previous_page: number | null;
  interpretation: {
    query: string;
    selected_place?: Record<string, string> | null;
    mode: 'text' | 'nearby';
    radius_km: number | null;
    latitude: number | null;
    longitude: number | null;
    distance_limited: boolean;
  };
  results: Array<Salon & {
    distance_km?: number | null;
    ranking?: { score: number; match_type: string };
  }>;
}

// Api RTK Query pour les salons
export const salonsApi = createApi({
  reducerPath: 'salonsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Salons', 'Favorites', 'Closures', 'Theme', 'Appearance'],
  endpoints: (builder) => ({
    // Endpoint pour récupérer la liste des salons
    salonsList: builder.query<Salon[], void | { ordering?: string, owner?: number, search?: string, language?: string }>({
      query: (queryArg) => {
        const paramsArg = (queryArg || {}) as { ordering?: string; owner?: number; search?: string; language?: string };
        return {
          url: `/api/salons/`,
          params: {
            ordering: paramsArg.ordering,
            owner: paramsArg.owner,
            search: paramsArg.search,
            language: paramsArg.language,
          },
        };
      },
      providesTags: ['Salons']
    }),
    
    // Endpoint pour récupérer un salon spécifique
    salonsRetrieve: builder.query<Salon, number | { salonId: number; language?: string }>({
      query: (queryArg) => {
        const salonId = typeof queryArg === 'number' ? queryArg : queryArg.salonId;
        const language = typeof queryArg === 'number' ? undefined : queryArg.language;
        return {
          url: `/api/salons/${salonId}/`,
          params: { language },
        };
      },
      providesTags: (result, error, queryArg) => {
        const id = typeof queryArg === 'number' ? queryArg : queryArg.salonId;
        return [{ type: 'Salons', id }];
      }
    }),

    salonThemeRetrieve: builder.query<SalonThemeState, number>({
      query: (salonId) => `/api/salons/${salonId}/theme/`,
      providesTags: (_result, _error, salonId) => [
        { type: 'Theme', id: salonId },
      ],
    }),
    salonThemeTargetsRetrieve: builder.query<
      SalonThemeTargetsResponse,
      { salonId: number; language?: string }
    >({
      query: buildSalonThemeTargetsRequest,
      providesTags: (_result, _error, { salonId }) => [
        { type: 'Theme', id: salonId },
      ],
    }),
    salonThemeUpdate: builder.mutation<
      SalonThemeState,
      { salonId: number; theme: SalonThemeConfig }
    >({
      query: ({ salonId, theme }) => ({
        url: `/api/salons/${salonId}/theme/`,
        method: 'PATCH',
        body: theme,
      }),
      invalidatesTags: (_result, _error, { salonId }) => [
        { type: 'Theme', id: salonId },
        { type: 'Salons', id: salonId },
      ],
    }),
    salonThemeApply: builder.mutation<
      SalonThemeApplyResponse,
      { salonId: number; theme: SalonThemeConfig; salonIds: number[] }
    >({
      query: buildSalonThemeApplyRequest,
      invalidatesTags: (_result, _error, { salonIds }) => (
        salonIds.flatMap((id) => [
          { type: 'Theme' as const, id },
          { type: 'Salons' as const, id },
        ])
      ),
    }),
    salonThemeReset: builder.mutation<SalonThemeState, number>({
      query: (salonId) => ({
        url: `/api/salons/${salonId}/theme/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, salonId) => [
        { type: 'Theme', id: salonId },
        { type: 'Salons', id: salonId },
      ],
    }),

    salonAppearanceRetrieve: builder.query<SalonAppearanceState, number>({
      query: buildSalonAppearanceRetrieveRequest,
      providesTags: (_result, _error, salonId) => [
        { type: 'Appearance', id: salonId },
      ],
    }),
    salonAppearanceTargetsRetrieve: builder.query<
      SalonAppearanceTargetsResponse,
      { salonId: number; language?: string }
    >({
      query: buildSalonAppearanceTargetsRequest,
      providesTags: (_result, _error, { salonId }) => [
        { type: 'Appearance', id: salonId },
      ],
    }),
    salonAppearanceUpdate: builder.mutation<
      SalonAppearanceState,
      {
        salonId: number;
        template_key: SalonPublicTemplateKey;
        theme: SalonThemeConfig;
        layout: SalonPublicLayoutConfig;
      }
    >({
      query: buildSalonAppearanceUpdateRequest,
      invalidatesTags: (_result, _error, { salonId }) => [
        { type: 'Appearance', id: salonId },
        { type: 'Salons', id: salonId },
      ],
    }),
    salonAppearanceApply: builder.mutation<
      SalonAppearanceApplyResponse,
      {
        salonId: number;
        template_key: SalonPublicTemplateKey;
        theme: SalonThemeConfig;
        layout: SalonPublicLayoutConfig;
        salonIds: number[];
      }
    >({
      query: buildSalonAppearanceApplyRequest,
      invalidatesTags: (_result, _error, { salonIds }) => (
        Array.from(new Set(salonIds)).flatMap((id) => [
          { type: 'Appearance' as const, id },
          { type: 'Salons' as const, id },
        ])
      ),
    }),
    salonAppearanceReset: builder.mutation<
      SalonAppearanceState,
      {
        salonId: number;
        template_key: SalonPublicTemplateKey;
        theme: SalonThemeConfig;
        layout: SalonPublicLayoutConfig;
      }
    >({
      query: buildSalonAppearanceResetRequest,
      invalidatesTags: (_result, _error, { salonId }) => [
        { type: 'Appearance', id: salonId },
        { type: 'Salons', id: salonId },
      ],
    }),
    
    // Endpoint pour récupérer les salons de l'utilisateur connecté
    salonsMySalonsRetrieve: builder.query<Salon[], void | { language?: string }>({
      query: (queryArg) => {
        const paramsArg = (queryArg || {}) as { language?: string };
        return {
          url: `/api/salons/my_salons/`,
          params: { language: paramsArg.language },
        };
      },
      providesTags: ['Salons']
    }),
    
    // Endpoint pour vérifier si un salon est en favoris
    checkFavorite: builder.query<FavoriteCheckResponse, number>({
      query: (salonId) => ({
        url: `/api/salons/favoris/check/`,
        params: { salon_id: salonId }
      }),
      providesTags: (result, error, salonId) => [{ type: 'Favorites', id: salonId }]
    }),
    
    // Endpoint pour toggle un salon en favoris (ajouter ou retirer)
    toggleFavorite: builder.mutation<FavoriteToggleResponse, number>({
      query: (salonId) => ({
        url: `/api/salons/favoris/toggle/`,
        method: 'POST',
        params: { salon_id: salonId }
      }),
      invalidatesTags: (result, error, salonId) => [{ type: 'Favorites', id: salonId }, 'Favorites']
    }),
    
    // Endpoint pour récupérer la liste des favoris de l'utilisateur
    favoritesList: builder.query<FavoriteItem[], void | { language?: string }>({
      query: (queryArg) => {
        const paramsArg = (queryArg || {}) as { language?: string };
        return {
          url: `/api/salons/favoris/`,
          params: { language: paramsArg.language },
        };
      },
      providesTags: ['Favorites']
    }),
    
    // Endpoint pour récupérer les disponibilités d'un salon
    salonAvailableSlots: builder.query<SalonAvailabilityResponse, { salonId: number; date: string; serviceId?: number }>({
      query: ({ salonId, date, serviceId }) => ({
        url: `/api/salons/favoris/salon_available_slots/`,
        params: {
          salon_id: salonId,
          date: date,
          ...(serviceId && { service_id: serviceId })
        }
      }),
      providesTags: (result, error, { salonId }) => [{ type: 'Salons', id: salonId }]
    }),

    // Endpoint pour récupérer le QR code d'un salon (owner only)
    salonQrRetrieve: builder.query<SalonQrResponse, number>({
      query: (salonId) => `/api/salons/${salonId}/qr/`,
    }),

    salonClosuresList: builder.query<SalonClosure[], { salonId: number }>({
      query: ({ salonId }) => `/api/salons/${salonId}/closures/`,
      providesTags: (result, error, arg) => [{ type: 'Closures', id: arg.salonId }],
    }),

    salonClosuresCreate: builder.mutation<SalonClosure, { salonId: number; body: SalonClosureCreateRequest }>({
      query: ({ salonId, body }) => ({
        url: `/api/salons/${salonId}/closures/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Closures', id: arg.salonId }],
    }),

    salonClosureUpdate: builder.mutation<SalonClosure, { salonId: number; closureId: number; body: Partial<SalonClosureCreateRequest> }>({
      query: ({ salonId, closureId, body }) => ({
        url: `/api/salons/${salonId}/closures/${closureId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Closures', id: arg.salonId }],
    }),

    salonClosureDelete: builder.mutation<{ ok: boolean } | void, { salonId: number; closureId: number }>({
      query: ({ salonId, closureId }) => ({
        url: `/api/salons/${salonId}/closures/${closureId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Closures', id: arg.salonId }],
    }),

    salonPublicClosuresList: builder.query<SalonClosure[], { salonId: number }>({
      query: ({ salonId }) => `/api/salons/${salonId}/public-closures/`,
      providesTags: (result, error, arg) => [{ type: 'Closures', id: arg.salonId }],
    }),

    suggestLocations: builder.query<
      LocationSuggestionsResponse,
      { q: string; language?: string; countryBias?: string }
    >({
      query: ({ q, language = 'fr', countryBias = 'dz' }) => ({
        url: '/api/locations/suggest/',
        params: {
          q,
          language,
          country_bias: countryBias,
        },
      }),
    }),

    reverseLocation: builder.mutation<
      ReverseLocationResponse,
      {
        latitude: number;
        longitude: number;
        language?: string;
        force_refresh?: boolean;
      }
    >({
      query: ({
        latitude,
        longitude,
        language = 'fr',
        force_refresh = false,
      }) => ({
        url: '/api/locations/reverse/',
        method: 'POST',
        body: {
          latitude,
          longitude,
          language,
          force_refresh,
        },
      }),
    }),

    searchSalonsV2: builder.query<SalonSearchResponse, SalonSearchRequest>({
      query: (params) => ({
        url: '/api/salons/search-salons-v2/',
        params,
      }),
    }),

    searchSalonSuggestions: builder.query<
      { results: SalonSearchSuggestion[] },
      { q: string; language?: string; countryBias?: string; limit?: number }
    >({
      query: ({
        q,
        language = 'fr',
        countryBias = 'dz',
        limit = 8,
      }) => ({
        url: '/api/salons/search-suggestions/',
        params: {
          q,
          language,
          country_bias: countryBias,
          limit,
        },
      }),
    }),
  }),
});

// Export des hooks générés automatiquement
export const {
  useSalonsListQuery,
  useSalonsRetrieveQuery,
  useSalonThemeRetrieveQuery,
  useSalonThemeTargetsRetrieveQuery,
  useSalonThemeUpdateMutation,
  useSalonThemeApplyMutation,
  useSalonThemeResetMutation,
  useSalonAppearanceRetrieveQuery,
  useSalonAppearanceTargetsRetrieveQuery,
  useSalonAppearanceUpdateMutation,
  useSalonAppearanceApplyMutation,
  useSalonAppearanceResetMutation,
  useSalonsMySalonsRetrieveQuery,
  useCheckFavoriteQuery,
  useToggleFavoriteMutation,
  useFavoritesListQuery,
  useSalonAvailableSlotsQuery,
  useLazySalonQrRetrieveQuery,
  useSalonClosuresListQuery,
  useSalonClosuresCreateMutation,
  useSalonClosureUpdateMutation,
  useSalonClosureDeleteMutation,
  useSalonPublicClosuresListQuery,
  useLazySuggestLocationsQuery,
  useReverseLocationMutation,
  useSearchSalonsV2Query,
  useLazySearchSalonSuggestionsQuery
} = salonsApi;
